import { DatePipe } from '@angular/common';
import { Component, OnInit, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { firstValueFrom } from 'rxjs';
import { ApiService } from '../core/api.service';

interface LogItem {
  Id: number;
  CreatedAt: string | null;
  HotelCode: string;
  Direction: string;
  MessageType: string;
  CRSResID: string | null;
  PMSResID: string | null;
  HttpStatus: number | null;
}

@Component({
  selector: 'app-logs',
  imports: [FormsModule, DatePipe],
  template: `
    <section class="card">
      <header class="head">
        <div>
          <h1>Logs</h1>
          <p>Search, inspect, and export API traffic.</p>
          @if (hotelCode()) {
            <div class="property-pill">Property {{ hotelCode() }}@if (hotelName()) { <span>· {{ hotelName() }}</span> }</div>
          }
        </div>
        <button type="button" class="secondary" (click)="exportCsv()">Export CSV</button>
      </header>

      <form class="filters" (submit)="apply($event)">
        <label><span>Search</span><input [(ngModel)]="q" name="q" placeholder="Correlation / CRS / PMS / HTTP"></label>
        <label><span>From</span><input [(ngModel)]="from" name="from" type="date"></label>
        <label><span>To</span><input [(ngModel)]="to" name="to" type="date"></label>
        <label><span>Direction</span>
          <select [(ngModel)]="dir" name="dir"><option value="all">All</option><option value="inbound">Inbound</option><option value="outbound">Outbound</option></select>
        </label>
        <label><span>Type</span>
          <select [(ngModel)]="type" name="type">
            <option value="all">All</option><option value="outbound">Outbound</option><option value="sync-response">Sync response</option><option value="async">Async</option><option value="ack">Ack</option>
          </select>
        </label>
        <label><span>Rows</span>
          <select [(ngModel)]="per" name="per"><option [ngValue]="10">10</option><option [ngValue]="25">25</option><option [ngValue]="50">50</option><option [ngValue]="100">100</option></select>
        </label>
        <div class="actions">
          <button type="submit">Filter</button>
          <button type="button" class="secondary" (click)="reset()">Reset</button>
        </div>
      </form>

      @if (error()) { <div class="error">{{ error() }}</div> }

      <div class="toolbar">Results <span>{{ total() }}</span></div>
      <div class="table-wrap">
        <table>
          <thead><tr><th>Created</th><th>Hotel</th><th>Dir</th><th>Type</th><th>CRSResID</th><th>PMSResID</th><th>HTTP</th><th>Actions</th></tr></thead>
          <tbody>
            @for (item of items(); track item.Id) {
              <tr>
                <td>{{ item.CreatedAt ? (item.CreatedAt | date:'yyyy-MM-dd HH:mm:ss') : '' }}</td>
                <td>{{ item.HotelCode }}</td>
                <td>{{ item.Direction }}</td>
                <td>{{ item.MessageType }}</td>
                <td>{{ item.CRSResID || '' }}</td>
                <td>{{ item.PMSResID || '' }}</td>
                <td>{{ item.HttpStatus ?? '' }}</td>
                <td class="actions">
                  <button type="button" class="secondary" (click)="viewPayload(item.Id)">View</button>
                  <button type="button" class="secondary" (click)="download(item.Id)">Download</button>
                </td>
              </tr>
            } @empty {
              <tr><td colspan="8">No logs match your filters.</td></tr>
            }
          </tbody>
        </table>
      </div>

      <footer class="pager">
        <div>Page {{ page() }} of {{ pages() }} — {{ total() }} rows</div>
        <div class="actions">
          <button type="button" class="secondary" (click)="go(1)" [disabled]="page() <= 1">« First</button>
          <button type="button" class="secondary" (click)="go(page() - 1)" [disabled]="page() <= 1">‹ Prev</button>
          <button type="button" class="secondary" (click)="go(page() + 1)" [disabled]="page() >= pages()">Next ›</button>
          <button type="button" class="secondary" (click)="go(pages())" [disabled]="page() >= pages()">Last »</button>
        </div>
      </footer>
    </section>

    @if (payloadOpen()) {
      <div class="overlay" (click)="closePayload($event)">
        <div class="modal">
          <header><h2>Payload</h2><button type="button" class="secondary" (click)="payloadOpen.set(false)">Close</button></header>
          <pre>{{ payloadText() }}</pre>
        </div>
      </div>
    }
  `,
  styles: `
    .card{background:#fff;border:1px solid var(--line);border-radius:16px;padding:16px;display:grid;gap:12px}
    .head,.pager{display:flex;justify-content:space-between;align-items:end;gap:16px;flex-wrap:wrap}
    h1,h2{margin:0}
    p{margin:4px 0 0;color:var(--muted)}
    .filters{display:grid;grid-template-columns:2fr repeat(5,minmax(0,1fr)) auto;gap:10px;align-items:end}
    label{display:grid;gap:6px}
    span{font-size:12px;color:#4e5c75;text-transform:uppercase;letter-spacing:.04em}
    input,select{height:40px;border:1px solid #c8d2e1;border-radius:10px;padding:0 12px}
    button{height:38px;border:0;border-radius:10px;background:var(--primary);color:#fff;padding:0 12px;cursor:pointer}
    .secondary{background:#6b778c}
    .toolbar span{display:inline-flex;padding:2px 8px;border-radius:999px;background:#edf3ff;color:#13356f;margin-left:6px}
    .property-pill{margin-top:8px;font-size:12px;color:#24416a;background:#edf3ff;border-radius:999px;display:inline-flex;padding:6px 10px;gap:6px}
    .property-pill span{color:#49668e}
    .table-wrap{overflow:auto}
    table{width:100%;border-collapse:collapse;min-width:980px}
    th,td{padding:10px;border-bottom:1px solid #edf0f6;text-align:left}
    .actions{display:flex;gap:8px;flex-wrap:wrap}
    .error{color:#a42323;background:#fdecec;border:1px solid #f4c0c0;border-radius:8px;padding:8px 10px}
    .overlay{position:fixed;inset:0;background:rgba(0,0,0,.5);display:flex;align-items:flex-start;justify-content:center;padding:40px;z-index:10}
    .modal{width:min(1000px,95vw);background:#fff;border-radius:14px;padding:16px;display:grid;gap:12px}
    .modal header{display:flex;justify-content:space-between;align-items:center}
    pre{white-space:pre-wrap;word-break:break-word;max-height:60vh;overflow:auto;background:#f6f8fc;border:1px solid #d8deea;padding:10px;border-radius:8px}
    @media (max-width: 1100px){ .filters{grid-template-columns:1fr 1fr} }
  `,
})
export class LogsComponent implements OnInit {
  private readonly api = inject(ApiService);
  readonly items = signal<LogItem[]>([]);
  readonly error = signal('');
  readonly payloadOpen = signal(false);
  readonly payloadText = signal('');
  readonly total = signal(0);
  readonly page = signal(1);
  readonly pages = signal(1);
  readonly hotelCode = signal('');
  readonly hotelName = signal('');

  q = '';
  dir = 'all';
  type = 'all';
  from = '';
  to = '';
  per = 25;

  async ngOnInit(): Promise<void> {
    const today = new Date();
    this.to = today.toISOString().slice(0, 10);
    const past = new Date(today);
    past.setDate(past.getDate() - 7);
    this.from = past.toISOString().slice(0, 10);
    try {
      const ctx = await firstValueFrom(this.api.get<{ activeProperty: { code: string; name: string } | null }>('/context/property'));
      this.hotelCode.set(ctx.activeProperty?.code || '');
      this.hotelName.set(ctx.activeProperty?.name || '');
    } catch {
      this.hotelCode.set('');
      this.hotelName.set('');
    }
    await this.load();
  }

  async load(targetPage = this.page()): Promise<void> {
    this.error.set('');
    const params = new URLSearchParams({
      page: String(targetPage),
      per: String(this.per),
      q: this.q,
      dir: this.dir,
      type: this.type,
      from: this.from,
      to: this.to,
    });
    try {
      const r = await firstValueFrom(this.api.get<{ total: number; items: LogItem[] }>(`/logs?${params.toString()}`));
      this.items.set(r.items ?? []);
      this.total.set(r.total ?? 0);
      this.page.set(targetPage);
      this.pages.set(Math.max(1, Math.ceil((r.total ?? 0) / this.per)));
    } catch {
      this.error.set('Failed to load logs.');
    }
  }

  async apply(event: Event): Promise<void> {
    event.preventDefault();
    await this.load(1);
  }

  async reset(): Promise<void> {
    this.q = '';
    this.dir = 'all';
    this.type = 'all';
    const today = new Date();
    this.to = today.toISOString().slice(0, 10);
    const past = new Date(today);
    past.setDate(past.getDate() - 7);
    this.from = past.toISOString().slice(0, 10);
    this.per = 25;
    await this.load(1);
  }

  async go(page: number): Promise<void> {
    await this.load(page);
  }

  async viewPayload(id: number): Promise<void> {
    try {
      const r = await firstValueFrom(this.api.get<{ payload: string }>(`/logs/${id}/payload`));
      this.payloadText.set(r.payload || '');
      this.payloadOpen.set(true);
    } catch {
      this.payloadText.set('Failed to load payload.');
      this.payloadOpen.set(true);
    }
  }

  async download(id: number): Promise<void> {
    const row = await firstValueFrom(this.api.get<{ payload: string; messageType: string }>(`/logs/${id}/payload`));
    const blob = new Blob([row.payload || ''], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `xmllog-${id}-${(row.messageType || 'payload').replace(/[^A-Za-z0-9_-]+/g, '_')}.txt`;
    a.click();
    URL.revokeObjectURL(url);
  }

  exportCsv(): void {
    const params = new URLSearchParams({ q: this.q, dir: this.dir, type: this.type, from: this.from, to: this.to });
    window.open(`/api/logs/export?${params.toString()}`, '_blank');
  }

  closePayload(e: MouseEvent): void {
    if (e.target === e.currentTarget) this.payloadOpen.set(false);
  }
}
